import { Component, OnInit, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueAccessorBase } from '../shared/value-accessor/value-accessor-base';
import { RestProviderService } from '../../../services/rest-provider/rest-provider.service';
import { RequestModel } from '../../../models/request/request-model';
import { firstValueFrom } from 'rxjs';

export interface ListingItem {
  Id: number;
  Title: string;
}

@Component({
    selector: 'sem-listing-items',
    templateUrl: './sem-listing-items.component.html',
    styleUrls: ['./sem-listing-items.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: SemListingItemsComponent, multi: true }
    ],
    standalone: false
})
export class SemListingItemsComponent extends ValueAccessorBase<string[]> implements OnInit {

  @Input() items: ListingItem[] = [];
  @Input() emptyMessage: string = '';
  @Input() apiEndpoint: string;
  @Input() payloadValues: string[];

  errorApiMsg: string;
  selectedItemIds: string[] = [];
  isLoading: boolean = false;
  hasError: boolean = false;
  
  async ngOnInit() {
    super.ngOnInit();
    
    // Set default empty message if not provided
    if (!this.emptyMessage) {
      this.emptyMessage = 'No items available';
    }
    
    // Load items from API if endpoint is provided
    if (this.apiEndpoint) {
      await this.loadItemsFromApi();
    } else {
      // If no API endpoint and no items provided, show empty list
      if (!this.items || this.items.length === 0) {
        this.items = [];
      }
    }

    // Initialize selected items from value
    if (this.value && Array.isArray(this.value)) {
      this.selectedItemIds = [...this.value];
    }
    
    // Initialize from model binding if available
    this.initializeFromModel();
  }

  onItemCheckboxChange(item: ListingItem, checked: boolean) {
    const itemId = item.Id.toString();
    
    if (checked) {
      // Add item ID to selected list if not already there
      if (!this.selectedItemIds.includes(itemId)) {
        this.selectedItemIds.push(itemId);
      }
    } else {
      // Remove item ID from selected list
      this.selectedItemIds = this.selectedItemIds.filter(id => id !== itemId);
    }

    // Update the component value and form validation
    this.value = [...this.selectedItemIds];
    this.updateModelBinding();
    this.updateFormValidation();
    
    // Trigger change detection
    this.touch();
  }


  isItemSelected(item: ListingItem): boolean {
    return this.selectedItemIds.includes(item.Id.toString());
  }

  private initializeFromModel() {
    // Initialize selected items from model binding
    if (this.model && this.bindingProperty) {
      const modelValue = this.dfms.getValue(this.model, this.bindingProperty);
      if (modelValue && Array.isArray(modelValue)) {
        // Extract Ids from array of objects
        this.selectedItemIds = modelValue.map(item => item.Id.toString());
        this.value = [...this.selectedItemIds];
      }
    }
  }

  private async loadItemsFromApi() {
    this.isLoading = true;
    this.hasError = false;
    this.errorApiMsg = "";

    try {
      // Use POST request with RequestModel as per codebase pattern
      const requestModel = new RequestModel();
      requestModel.url = this.apiEndpoint;
      requestModel.contentType = "application/json";
      requestModel.responseType = "application/json";

      // Build payload from payloadValues configuration
      const payload: any = {};
      
      if (this.payloadValues && this.payloadValues.length > 0) {
        for (const payloadItem of this.payloadValues) {
          if (payloadItem["FieldName"] && payloadItem["Value"]) {
            // Get value from model using dfms.getValue
            let fieldValue = this.dfms.getValue(this.dfms.globalModel, payloadItem["Value"]);
            if (fieldValue == null || typeof fieldValue == "undefined") {
              fieldValue = this.dfms.getValue(this.model, payloadItem["Value"]);
            }
            if (fieldValue) {
              payload[payloadItem["FieldName"]] = fieldValue;
            }
          }
        }
      }

      requestModel.data = { "Envelope": payload };

      const response = await firstValueFrom(this.restProviderService.getDataPOST<any>(requestModel));
      
      if (response && response.Metadata && response.Metadata.ServerMessageMetadata) {
        const messageType = response.Metadata.ServerMessageMetadata.MessageType;
        
        if (messageType === 1) {
          // Success - parse items from response
          const items = this.parseItemsFromResponse(response);
          if (items && items.length > 0) {
            this.items = items;
          } else {
            // Empty response, use empty array
            this.items = [];
          }
        } else if (messageType === 2) {
          // Error from server
          this.hasError = true;
          // Show empty list on error
          this.items = [];
          this.errorApiMsg = response.Metadata.ServerMessageMetadata.Message;
        } else {
          // Unknown message type
          this.hasError = true;
          this.errorApiMsg = "Error loading items from server";
          this.items = [];
        }
      } else {
        this.hasError = true;
        this.errorApiMsg = "Error loading items from server";
        this.items = [];
      }
    } catch (error) {
      console.error('Error loading items from API:', error);
      this.hasError = true;
      this.errorApiMsg = "Error loading items from server";
      // Show empty list on error
      this.items = [];
    } finally {
      this.isLoading = false;
    }
  }

  private parseItemsFromResponse(response: any): ListingItem[] {
    try {
      // Try to get items from different possible locations in response
      let itemsData = null;
      
      if (response.Model) {
        itemsData = response.Model["data"];
      } else if (response.Metadata && response.Metadata.ServerMessageMetadata && response.Metadata.ServerMessageMetadata.Message) {
        // If message contains JSON string of items
        try {
          itemsData = JSON.parse(response.Metadata.ServerMessageMetadata.Message);
        } catch (e) {
          // Message is not JSON, might be error text
          return [];
        }
      } else if (Array.isArray(response)) {
        itemsData = response;
      }

      if (itemsData && Array.isArray(itemsData)) {
        return itemsData.map(item => ({
          Id: item.Id,
          Title: item.Title
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error parsing items from response:', error);
      return [];
    }
  }


  private updateModelBinding() {
    // Update this.model[bindingProperty] with array of objects containing Id
    if (this.model && this.bindingProperty) {
      const selectedObjects = this.selectedItemIds.map(id => ({ Id: parseInt(id) }));
      this.dfms.assign(this.model, this.bindingProperty, selectedObjects, true);
    }
  }

  private updateFormValidation() {
    const attName = this.getAttribute('name');
    
    if (attName) {
      let control = this.dfms.globalForm.controls[attName];
      
      if (control !== null && typeof control !== 'undefined') {
        this.dfms.globalForm.controls[attName].setValue(this.selectedItemIds);
        this.dfms.globalForm.controls[attName].updateValueAndValidity();
      }
      
      this.dfms.updateControlsValidationArray(attName, this.selectedItemIds);
    }

    if (typeof this.onValueChangeEval !== 'undefined' && this.onValueChangeEval) {
      // Check if the string starts and ends with double quotes and remove them
      if (this.onValueChangeEval.startsWith('"') && this.onValueChangeEval.endsWith('"')) {
        this.onValueChangeEval = this.onValueChangeEval.substring(1, this.onValueChangeEval.length - 1);
      }
      eval(this.onValueChangeEval);
    }
  }


  protected override valueChanged(): void {
    // Update selected items when value changes externally
    if (this.value && Array.isArray(this.value)) {
      this.selectedItemIds = [...this.value];
      //this.updateSelectAllState();
    }
  }
}